import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import { showModal } from 'modules/common/modal';
import placeholder from 'assets/images/no-image-placeholder.svg';

const IconView = () => (
  <svg
    className="h-5 w-5 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const IconLoader = () => (
  <svg className="animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

function ViewVideo({
  src,
  type,
  className,
  hoverLabel,
  defaultSrc,
  withLightBox,
  heightClassName,
  withFooterContent,
  containerClassName,
  disableDefaultImage,
}) {
  const [url, setUrl] = React.useState(src);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const [srcError, setSrcError] = React.useState(false);

  let timer;

  const handleOnViewVideo = (e) => {
    e.preventDefault();
    showModal({
      title: false,
      escButton: true,
      modalSize: 'modal-xs',
      containerClassName: 'p-5 bg-white',
      focusTrap: false,
      content: (onClose) => (
        <div className="flex flex-col gap-3 md:gap-5">
          <div className="flex mx-auto w-full max-w-sm">
            <div className="h-full w-full aspect-h-16 aspect-w-9">
              <video controls autoPlay height="100%" width="100%">
                <source src={url} type={type} />
                <track kind="captions" />
                Sorry, your browser doesn&apos;t support embedded videos.
              </video>
            </div>
          </div>
          {withFooterContent && (
            <div className="flex flex-col">
              {typeof withFooterContent === 'function'
                ? withFooterContent?.(onClose)
                : withFooterContent}
            </div>
          )}
        </div>
      ),
    });
  };

  const handleError = () => {
    setSrcError(true);
    timer = setTimeout(() => {
      setUrl(defaultSrc);
      setVideoLoaded(true);
    }, 700);
  };

  const handleLoad = () => {
    setSrcError(false);
    timer = setTimeout(() => {
      setVideoLoaded(true);
    }, 100);
  };

  React.useLayoutEffect(() => {
    setUrl(src);
    return () => {
      setUrl('');
      clearTimeout(timer);
    };
  }, [src, timer]);

  return (
    <div
      className={cn(
        'group relative overflow-hidden flex items-center justify-center',
        {
          [`${containerClassName}`]: containerClassName,
          'bg-[#f8f8f8] w-full': !containerClassName,
        }
      )}
    >
      {!videoLoaded && (
        <div className="absolute h-full w-full">
          <div className="flex h-full w-full justify-center items-center">
            <div className="w-4 h-4">
              <IconLoader />
            </div>
          </div>
        </div>
      )}
      {withLightBox && videoLoaded && !srcError && (
        <div
          role="presentation"
          className={cn(
            'cursor-zoom-in absolute h-full w-full transition duration-300 opacity-0 group-hover:opacity-100 group-hover:bg-gray-900 group-hover:bg-opacity-75'
          )}
        >
          <div className="flex h-full w-full justify-center items-center text-white text-xxs font-medium">
            {hoverLabel}
          </div>
        </div>
      )}
      <img
        src={url ?? ''}
        alt=""
        className={cn({
          invisible: !srcError,
          visible: srcError,
          [`${heightClassName}`]: heightClassName,
          [`${className}`]: className,
        })}
      />
      <video
        className={cn(
          {
            invisible: !videoLoaded,
            visible: videoLoaded,
            [`${heightClassName}`]: heightClassName,
            [`${className}`]: className,
            hidden: (disableDefaultImage && url === defaultSrc) || srcError,
          },
          {
            'cursor-pointer': withLightBox,
          }
        )}
        onLoadedData={handleLoad}
        onClick={withLightBox ? handleOnViewVideo : () => {}}
        onError={handleError}
      >
        <source src={`${url}#t=0.1`} type={type} />
        <track kind="captions" />
        Sorry, your browser doesn&apos;t support embedded videos.
      </video>
    </div>
  );
}

ViewVideo.defaultProps = {
  heightClassName: 'h-auto',
  src: '',
  type: 'video/mp4',
  containerClassName: false,
  disableDefaultImage: false,
  className: 'object-fit w-full',
  hoverLabel: (
    <>
      <IconView /> Play Video.
    </>
  ),
  withLightBox: true,
  withFooterContent: false,
  defaultSrc: placeholder, // https://via.placeholder.com/128?text=Img,
};

ViewVideo.propTypes = {
  src: PropTypes.string,
  type: PropTypes.string,
  defaultSrc: PropTypes.string,
  heightClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  containerClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  hoverLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  withFooterContent: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.func,
  ]),
  withLightBox: PropTypes.bool,
  disableDefaultImage: PropTypes.bool,
};

export default React.memo(ViewVideo);
